import {
  getLeoUuid,
  LeoUUID,
  getObject,
  getOptionalObject,
  getList,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { Code, CodeDTO } from "../types/code";
import { SurveyRuleAction } from "./surveyRuleAction";
import { SurveyRuleTarget, SurveyRuleTargetDTO } from "./surveyRuleTarget";
/* eslint-disable import/export */
export abstract class GetSurveyRuleTargetOptionsRPC implements
  LeoRPC<GetSurveyRuleTargetOptionsRPC.Request, GetSurveyRuleTargetOptionsRPC.Response, GetSurveyRuleTargetOptionsRPC.Errors.Errors>
{
  abstract execute(
    request: GetSurveyRuleTargetOptionsRPC.Request
  ): Promise<LeoRPCResult<GetSurveyRuleTargetOptionsRPC.Response, GetSurveyRuleTargetOptionsRPC.Errors.Errors>>;
}

export namespace GetSurveyRuleTargetOptionsRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    surveyId: string;
    questionCode?: CodeDTO | null;
    action: SurveyRuleAction.SurveyRuleActionDTO;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isGetSurveyRuleTargetOptionsRPCRequest: boolean = true;
  
    readonly surveyId: LeoUUID;
    readonly questionCode: Code | null;
    readonly action: SurveyRuleAction.SurveyRuleAction;
  
  
    constructor(
    surveyId: LeoUUID, 
    questionCode: Code | null = null, 
    action: SurveyRuleAction.SurveyRuleAction
    ) {
      this.surveyId = surveyId;
      this.questionCode = questionCode;
      this.action = action;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const surveyId = getLeoUuid(dto, "surveyId", InvalidRequestSurveyIdError);
      let questionCode = getOptionalObject(dto, "questionCode");
      if(questionCode !== undefined && questionCode !== null) {
        questionCode = Code.fromDTO(getObject(dto, "questionCode", InvalidRequestQuestionCodeError));
      }
      const action = SurveyRuleAction.fromDTO(getObject(dto, "action", InvalidRequestActionError));
      return new Request(
        surveyId,
        questionCode,
        action
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          surveyId: this.surveyId.toDTO(),
          action: SurveyRuleAction.toDTO(this.action)
      };
      if (this.questionCode !== undefined && this.questionCode !== null){
        dto.questionCode = this.questionCode.toDTO();
      }
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `GetSurveyRuleTargetOptionsRPC(surveyId='${this.surveyId}',questionCode='${this.questionCode}',action='${this.action}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    targets: SurveyRuleTargetDTO[];
  }
  
  export class Response implements LeoRPCResponse {
    readonly isGetSurveyRuleTargetOptionsResponse: boolean = true;
  
    readonly targets: SurveyRuleTarget[];
  
  
    constructor(
    targets: SurveyRuleTarget[]
    ) {
      this.targets = targets;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const targets = getList<SurveyRuleTargetDTO, SurveyRuleTarget>(
        dto,
        "targets",
        SurveyRuleTarget.fromDTO,
        InvalidResponseTargetsError
      );
      return new Response(
        targets
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          targets: this.targets.map((e: SurveyRuleTarget) => { return e.toDTO() })
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `GetSurveyRuleTargetOptions(targets='${this.targets}')`;
  };
  

  export enum RPCError {
    InvalidSurveyId = "INVALID_SURVEY_ID",
    InvalidQuestionCode = "INVALID_QUESTION_CODE",
    UnsupportedQuestionType = "UNSUPPORTED_QUESTION_TYPE"
  }

  export namespace Errors {
    
    export interface InvalidSurveyIdDTO {
      code: string;
    }
    
    export class InvalidSurveyId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyIdDTO {
        const dto: InvalidSurveyIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyId.prototype.toString = function toString(): string {
      return `GetSurveyRuleTargetOptionsRPC.INVALID_SURVEY_ID()`;
    };
    
    
    export interface InvalidQuestionCodeDTO {
      code: string;
    }
    
    export class InvalidQuestionCode extends LeoRPCError<RPCError> {
      readonly isInvalidQuestionCode: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidQuestionCode;
        super(code);
      }
    
    
      toDTO(): InvalidQuestionCodeDTO {
        const dto: InvalidQuestionCodeDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidQuestionCode.prototype.toString = function toString(): string {
      return `GetSurveyRuleTargetOptionsRPC.INVALID_QUESTION_CODE()`;
    };
    
    
    export interface UnsupportedQuestionTypeDTO {
      code: string;
    }
    
    export class UnsupportedQuestionType extends LeoRPCError<RPCError> {
      readonly isUnsupportedQuestionType: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.UnsupportedQuestionType;
        super(code);
      }
    
    
      toDTO(): UnsupportedQuestionTypeDTO {
        const dto: UnsupportedQuestionTypeDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    UnsupportedQuestionType.prototype.toString = function toString(): string {
      return `GetSurveyRuleTargetOptionsRPC.UNSUPPORTED_QUESTION_TYPE()`;
    };
    

    export type Errors = InvalidSurveyId | InvalidQuestionCode | UnsupportedQuestionType;
    export type ErrorsDTO = InvalidSurveyIdDTO | InvalidQuestionCodeDTO | UnsupportedQuestionTypeDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidSurveyId:
          return new InvalidSurveyId();
        case RPCError.InvalidQuestionCode:
          return new InvalidQuestionCode();
        case RPCError.UnsupportedQuestionType:
          return new UnsupportedQuestionType();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestSurveyIdError extends GetSurveyRuleTargetOptionsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyIdError";
  }
}

export class InvalidRequestQuestionCodeError extends GetSurveyRuleTargetOptionsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestQuestionCodeError";
  }
}

export class InvalidRequestActionError extends GetSurveyRuleTargetOptionsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestActionError";
  }
}

export class InvalidResponseTargetsError extends GetSurveyRuleTargetOptionsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseTargetsError";
  }
}

export class InvalidResponseInvalidSurveyIdError extends GetSurveyRuleTargetOptionsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyIdError";
  }
}

export class InvalidResponseInvalidQuestionCodeError extends GetSurveyRuleTargetOptionsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidQuestionCodeError";
  }
}

export class InvalidResponseUnsupportedQuestionTypeError extends GetSurveyRuleTargetOptionsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseUnsupportedQuestionTypeError";
  }
}

